import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { socketClient } from '@/lib/socket'
import { useToast } from './use-toast'
import type { RealtimeUpdate, Notification } from '@/types'

export function useSocket() {
  const [isConnected, setIsConnected] = useState(false)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  useEffect(() => {
    // Connect to socket
    socketClient.connect()
    setIsConnected(socketClient.isConnected())

    // Check connection status periodically
    const interval = setInterval(() => {
      setIsConnected(socketClient.isConnected())
    }, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [])

  // Listen for entity updates
  useEffect(() => {
    const handleEntityUpdate = (update: RealtimeUpdate) => {
      // Invalidate relevant queries based on entity type
      queryClient.invalidateQueries({ queryKey: [update.entity_type + 's'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })

      // Show toast notification for updates
      if (update.action === 'created') {
        toast({
          title: 'New item created',
          description: `A new ${update.entity_type} has been created.`,
        })
      }
    }

    socketClient.onEntityUpdate(handleEntityUpdate)

    return () => {
      socketClient.offEntityUpdate(handleEntityUpdate)
    }
  }, [queryClient, toast])

  // Listen for content linked events (e.g., attachments copied to order on Closed Won)
  useEffect(() => {
    const handleContentLinked = (data: { linked_entity_id: string }) => {
      queryClient.invalidateQueries({ queryKey: ['content', 'entity', data.linked_entity_id] })
    }

    socketClient.onContentLinked(handleContentLinked)

    return () => {
      socketClient.offContentLinked(handleContentLinked)
    }
  }, [queryClient])

  // Listen for notifications
  useEffect(() => {
    const handleNotification = (notification: Notification) => {
      console.log('[Socket] Notification received:', notification)
      // Delay to avoid being swallowed by success toasts from mutations
      setTimeout(() => {
        toast({
          title: notification.title,
          description: notification.message,
          variant: notification.type === 'error' ? 'destructive' : 'default',
        })
      }, 2000)
    }

    socketClient.onNotification(handleNotification)

    return () => {
      socketClient.offNotification(handleNotification)
    }
  }, [toast])

  return {
    isConnected,
    socket: socketClient,
  }
}

export function useRealtimeEntity(entityType: string, entityId?: string) {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!entityId) return

    const room = `${entityType}:${entityId}`
    socketClient.joinRoom(room)

    const handleUpdate = (update: RealtimeUpdate) => {
      if (update.entity_type === entityType && update.data.id === entityId) {
        queryClient.invalidateQueries({ queryKey: [entityType + 's', entityId] })
      }
    }

    socketClient.onEntityUpdate(handleUpdate)

    return () => {
      socketClient.leaveRoom(room)
      socketClient.offEntityUpdate(handleUpdate)
    }
  }, [entityType, entityId, queryClient])
}
